import React, {MutableRefObject, useContext, useEffect, useMemo, useRef, useState} from "react";
import ReactQuill, {Quill} from "react-quill";
import {FileService} from "app/services/common/file";
import {ResponseResultCode} from "app/model/response-api";
import {AtomFileModel} from "app/model/common/file";
import {UNSAFE_NavigationContext} from "react-router-dom";
import styled from "@emotion/styled";

interface ArcEditorProps {
  id?: string;
  editorRef?: MutableRefObject<ReactQuill>;
  value: any;
  onChange: (content: string) => void;
  savedPagePath: string;
  imgServerPath: string;
  height?: string;
  placeholder?: string;
}

const QuillWrapper = styled.div<{editorHeight?: string}>`
  .ql-editor {
    height: inherit;
    min-height: ${(props: any) => props.editorHeight ?? '22rem'};
    max-height: 22rem;
    overflow: auto;
  }

  .ql-editor b {
    font-weight: bold;
  }

  .ql-editor i {
    font-style: italic;
  }
`;

export default function ArcEditor(props: Readonly<ArcEditorProps>) {
  const { height = '22rem' } = props;

  const navigator = useContext(UNSAFE_NavigationContext).navigator as any;
  const defaultEditorRef: any = useRef<typeof ArcEditor>();
  const quillInstance = props.editorRef ?? defaultEditorRef;

  // apply bold option
  const bold = Quill.import('formats/bold');
  bold.tagName = 'b';
  Quill.register(bold, true);

  // apply italic option
  const italic = Quill.import('formats/italic');
  italic.tagName = 'i';
  Quill.register(italic, true);

  const handleKeydown = (event : any) => {
    if (event.key === 'Escape' && quillInstance.current) {
      quillInstance.current.blur();
    }
  };

  const quillModule = useMemo(() => {
    return {
      toolbar: {
        container: [
          [{size: ['small', false, 'large', 'huge']}],
          ['bold', 'italic', 'underline', 'strike'],
          [{list: 'ordered'}, {list: 'bullet'}],
          ['clean'],
          [{color: []}, {background: []},],
          ['image'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    };
  }, []);

  const [content, setContent] = useState<string>(props.value);
  const initialQuillImageState = () => ArcEditorUtil.getImageFromContent(props.value);
  const [quillImageState, setQuillImageState] = useState<AtomFileModel[]>(initialQuillImageState)


  useEffect(() => {
    (() => {
      window.onbeforeunload = null;
      window.addEventListener('beforeunload', deleteUnsavedImageBeforeRefresh);
    })();
    return () => {
      deleteUnsavedImageBeforePageMove(props.savedPagePath);
      window.removeEventListener('beforeunload', deleteUnsavedImageBeforeRefresh);
    }
  }, []);

  function imageHandler() {
    const quill = quillInstance.current;
    if (quill) {
      quill.focus();
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");
      input.defaultValue = "";
      input.onchange = (e: any) => imgFileChangedEvent(e);
      input.click();
    }
  }

  async function imgFileChangedEvent(e: any) {
    const inputFile = e.target.files[0];

    const editor = quillInstance.current?.getEditor();
    if (inputFile && editor) {
      await readAndUploadFile(inputFile);
    }
  }

  async function readAndUploadFile(file: File) {
    let files: AtomFileModel[] = [];
    const formData: FormData = new FormData();
    formData.append('files', file);
    const response = await FileService.getInstance().upload(props.imgServerPath, formData);
    if (response && ResponseResultCode.Y === response.resultCode) {
      files = response.data ?? [];
      if (response.data && response.data.length > 0) {
        setImageToEditor(response.data);
      }
    }
    return files;
  }

  function setImageToEditor(files: AtomFileModel[]) {
    const editor = quillInstance.current?.getEditor();
    if (editor) {
      for (const file of files) {
        const range = editor.getSelection();
        if (range) {
          let imgInfo = quillImageState;
          imgInfo.push(file);
          setQuillImageState(imgInfo);

          const param = `?filePath=${file.filePath}&fileName=${file.fileName}`;
          const url = encodeURI(props.imgServerPath + '/files/image' + param);
          editor.insertEmbed(range.index, "image", `${url}`, "user");
          editor.setSelection(range.index + 1, 1);
        }
      }
    }
  }

  function handleContentChange(value: any) {
    const limit = 3000;
    const quill = quillInstance.current;
    if (quill?.editor.getLength() > limit) {
      quill?.editor.deleteText(limit, quill?.editor.getLength());
    } else {
      checkContentImageIsDeletedAndProcess(value);
      setContent(value);
      props.onChange(value);
    }
  }

  // Delete Image from server
  // When navigate changes before saved
  function deleteUnsavedImageBeforePageMove(savedPagePath: string) {
    const navigateAction = navigator.action;
    const futureLocation = navigator.location.pathname;
    if ('POP' === navigateAction || ('PUSH' === navigateAction && savedPagePath !== futureLocation)) {
      deleteUnsavedImageBeforeRefresh();
    }
  }

  // Delete Image from server
  // When window refresh
  function deleteUnsavedImageBeforeRefresh() {
    const initCttImg = ArcEditorUtil.getImageFromContent(props.value);
    const newCttImg = quillImageState;

    if (initCttImg.length < newCttImg.length) {
      const diffImg = ArcEditorUtil.getDiffImages(newCttImg, initCttImg);
      if (diffImg && diffImg.length > 0) {
        FileService.getInstance().deleteAtomFile(props.imgServerPath, diffImg)
        .then(response => {
          if (response && ResponseResultCode.Y === response.resultCode) {
            setQuillImageState(ArcEditorUtil.getImageFromContent(props.value ?? "") ?? []);
          }
        });
      }
    }
  }

  // Delete Image from server.
  // When editor content changes like escape or delete
  function checkContentImageIsDeletedAndProcess(value: string) {
    const images = ArcEditorUtil.getImageFromContent(value);
    if (images.length < quillImageState.length) {
      const diffImages = ArcEditorUtil.getDiffImages(quillImageState, images);
      if (diffImages && diffImages.length > 0) {
        FileService.getInstance().deleteAtomFile(props.imgServerPath, diffImages)
        .then(response => {
          if (response && ResponseResultCode.Y === response.resultCode) {
            setQuillImageState(images);
          }
        });
      }
    }
  }

  return (
    <QuillWrapper editorHeight={height}>
      <ReactQuill theme="snow"
                  id={props.id ?? crypto.randomUUID()}
                  ref={quillInstance}
                  value={content}
                  placeholder={props.placeholder ?? "내용을 입력하세요."}
                  onChange={handleContentChange}
                  onKeyDown={handleKeydown}
                  modules={quillModule} />
    </QuillWrapper>
  );
}

export class ArcEditorUtil {

  public static getImageFromContent(content: string) {
    const dummy = document.createElement('div');
    dummy.innerHTML = content;
    const images = Array.from(dummy.querySelectorAll("img[src]"));
    return images.map((item: any) => {
      const url = new URL(item.src);
      url.searchParams.get("filePath")
      return {
        filePath: decodeURI(url.searchParams.get("filePath") ?? ""),
        fileName: decodeURI(url.searchParams.get("fileName") ?? "")
      } as AtomFileModel
    });
  }

  public static getDiffImages(long: AtomFileModel[], short: AtomFileModel[]) {
    return long.filter(f1 => {
      return short.findIndex(f2 => f1.filePath === f2.filePath && f1.fileName === f2.fileName) < 0;
    });
  }
}